<script setup lang="ts">
import * as yup from 'yup'
import SmallModel from '@/components/Interface/Modal/ModelSizes/SmallModel.vue'
import { usePlanetStore } from '@/stores/planetStore.ts'
import { computed, reactive } from 'vue'
import { useGameStore } from '@/stores/gameStore.ts'
import Backend from '@/models/backend.ts'
import FormProcess from '@/models/formProcess.ts'
import FormInput from '@/components/Interface/Modal/Forms/FormInput.vue'
import FormGenericError from '@/components/Interface/Modal/Forms/FormGenericError.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'

/**
 * Stores / models
 */
const gameStore = useGameStore()
const planetStore = usePlanetStore()
const planetData = computed(() => planetStore.findPlanetById(gameStore.modalData.planetId)!)

const backend = new Backend()
const formProcess = new FormProcess()

/**
 * Functions
 */
// Define the validation schema
const schema = yup.object().shape({
  planet_name: yup
    .string()
    .min(3, () => `Use at least 3 characters`)
    .max(32, () => `Do not use more than 32 characters`)
    .required(() => `This is required`)
})

// Default form values
const formValues = reactive<{ [key: string]: string }>({
  planet_name: planetData.value?.planetName || ''
})

// Form errors
const formErrors = reactive<{ [key: string]: string }>({
  generic: '',
  planet_name: ''
})

// Handle form submission
const submit = () => {
  schema
    .validate(formValues)
    .then(() => {
      formProcess.resetFormErrors(formErrors)
      backend
        .updatePlanetName(gameStore.modalData.planetId, formValues.planet_name)
        .then(() => formProcess.processBackendSuccessResponse())
        .catch((error) => formProcess.processBackendErrorResponse(error, formErrors))
    })
    .catch((error) => formProcess.processFrontendErrorResponse(error, formErrors))
}
</script>

<template>
  <SmallModel title="Edit">
    <form method="post" @submit.prevent="submit" class="pl-4 pr-4 pt-4">
      <FormInput
        label="Planet name"
        v-model="formValues.planet_name"
        :error="formErrors.planet_name"
        :autoFocus="true"
      />
      <div class="flex justify-between py-2">
        <FormGenericError :error="formErrors.generic" />
        <ActionButton type="submit" class="hidden md:block">Edit</ActionButton>
      </div>
    </form>
  </SmallModel>
</template>

<style scoped lang="scss"></style>
